import { useEffect, useState } from "react";
import { BadgeCheck, IdCard, Loader2, ShieldAlert, X } from "lucide-react";
import { useAdminApi } from "../lib/api";
import { useI18n, serverError } from "../i18n";

/**
 * 證件審核
 * ═══════════════════════════════════════════════════════════════
 *
 * 這是整個後台唯一會顯示身分證影像的畫面，所以它的規則跟其他頁不同。
 *
 * ── 影像預設不顯示 ──────────────────────────────────────────
 *
 * 要按一下才載入。清單一打開就把十張身分證鋪在螢幕上，
 * 等於在辦公室、咖啡廳、共乘車上對著旁邊的人展示 ——
 * 而審核者多半只需要處理其中一兩筆。
 *
 * 而且 identity 對**每一次檢視**都寫一筆稽核，
 * 自動載入會讓那份日誌變成噪音，真正該被注意的反覆翻閱就藏得住了。
 *
 * ── 核准一定要填 personKey ─────────────────────────────────
 *
 * 那是「一個真人一個帳號」真正的執行點，UNIQUE 索引建在它上面。
 * 同一個人第二次來，審核者填同一個值就會撞上 —— 那正是我們要的。
 *
 * 現在由人填（例如「證件種類 + 號碼」的雜湊，規則由審核流程定）。
 * 將來接 Stripe Identity / Persona 這類服務時，
 * 直接改成填它們回傳的 person id，其他地方一行都不用動。
 *
 * ── 為什麼沒有「證件號碼」欄位 ──────────────────────────────
 *
 * 七個市場有五個做不到或法律不准儲存，詳見
 * services/identity/migrations/0017_identity_docs.sql。
 * 審核者看得到影像上的號碼，但那個號碼不進資料庫。
 */
interface Pending {
  id: string;
  userId: string;
  status: string;
  countryCode: string | null;
  docType: string | null;
  submittedAt: string;
  handle: string;
  displayName: string;
  email: string;
}

export default function Identity() {
  const { t } = useI18n();
  const { data, error, loading } = useAdminApi<Pending[]>("/identity");
  const [done, setDone] = useState<Record<string, string>>({});

  if (loading) {
    return (
      <div className="space-y-4">
        <Heading />
        {[0, 1].map((i) => (
          <div key={i} className="h-40 rounded-card shimmer" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <Heading />
        <p className="rounded-card nm-inset p-5 text-sm text-brand-800">{error}</p>
      </div>
    );
  }

  const items = (data ?? []).filter((row) => !done[row.id]);

  return (
    <div className="space-y-4">
      <Heading count={items.length} />

      {items.length === 0 ? (
        <div className="rounded-card nm p-8 text-center">
          <BadgeCheck className="mx-auto size-6 text-ink-muted" />
          <p className="mt-3 text-sm text-ink-soft">{t("idv.empty")}</p>
        </div>
      ) : (
        items.map((row) => (
          <Row key={row.id} row={row} onDone={(status) => setDone((d) => ({ ...d, [row.id]: status }))} />
        ))
      )}
    </div>
  );
}

function Heading({ count }: { count?: number }) {
  const { t } = useI18n();
  return (
    <div>
      <h1 className="flex items-center gap-2 text-lg font-semibold text-ink">
        <IdCard className="size-4" />
        {t("nav.identity")}
        {count !== undefined && count > 0 && (
          <span className="rounded-pill bg-brand-600/20 px-2 py-0.5 text-[11px] font-medium text-brand-800">
            {count}
          </span>
        )}
      </h1>
      <p className="mt-1.5 text-xs leading-relaxed text-ink-muted">
        {t("idv.lede")}
      </p>
    </div>
  );
}

/*
  證件種類。核准時要選 —— 它決定號碼的樣子，也是將來要不要
  改接 KYC 供應商時的分流依據。
*/
/**
 * 發證國別。目前開放的市場 + 常見來源國，不用打字。
 *
 * 名稱不寫死：四個語系各維護一份手寫清單一定會過時，
 * 而瀏覽器內建的 Intl.DisplayNames 本來就有一份，
 * 而且是照使用者當下的語系給的。
 */
const COUNTRY_CODES = ["TW", "JP", "KR", "US", "GB", "ES", "SG", "AU", "HK", "MY", "CN"];

const DOC_TYPES = [
  { value: "national_id", key: "idv.doc.nationalId" },
  { value: "passport", key: "idv.doc.passport" },
  { value: "driver_license", key: "idv.doc.driverLicense" },
  { value: "residence_permit", key: "idv.doc.residencePermit" },
] as const;

/*
  駁回理由用選單，不用自由文字。
  ═══════════════════════════════════════════════════════════════

  三個理由：

    一致性  同一種問題每次都給同一句話。自由文字會讓「照片太模糊」
            在不同審核者手上變成五種說法，而使用者看到的是最終那一句。

    可翻譯  固定的幾條將來可以進字典，自由文字永遠只有中文。

    速度    審核是重複勞動。每一筆都要想怎麼措辭，是最先被放棄的環節 ——
            放棄之後就會出現空白理由或「不符規定」這種等於沒說的話。

  每一條都寫成「使用者看得懂、而且知道下一步該做什麼」。
*/
/*
  ── 送出去的是代號，不是句子 ────────────────────────────────

  上面那段註解裡的「可翻譯」原本只是願望：選單存的是中文句子，
  伺服器原封不動存起來，前台原封不動顯示 —— 於是一個德國接案者
  收到的駁回理由是中文，而那句話的全部作用就是告訴他該怎麼重拍。

  現在選單的 value 是代號（idr.blurry 之類），前台用自己的字典
  翻成使用者的語言。後台這邊的 label 也走字典，所以日本審核者
  看到的是日文選項。

  舊資料是自由文字，前台認不出代號時原樣顯示 —— 不需要遷移。
*/
const REJECT_REASONS = [
  "idr.blurry",
  "idr.cropped",
  "idr.glare",
  "idr.sameSide",
  "idr.expired",
  "idr.notAccepted",
  "idr.nameMismatch",
] as const;

const field =
  "mt-1.5 w-full rounded-xl nm-inset px-3 py-2 text-xs text-ink outline-none";

function Row({ row, onDone }: { row: Pending; onDone: (status: string) => void }) {
  const { t, locale } = useI18n();
  const [shown, setShown] = useState(false);

  /* 國別名稱照審核者的語系顯示，清單本身只存代碼 */
  const countryName = (code: string) =>
    new Intl.DisplayNames([locale], { type: "region" }).of(code) ?? code;

  const [docType, setDocType] = useState("");
  const [docNumber, setDocNumber] = useState("");
  const [docCountry, setDocCountry] = useState(row.countryCode ?? "TW");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function review(approve: boolean) {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`/api/identity/${row.id}`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          approve,
          ...(approve
            ? {
                docType,
                docNumber: docNumber.trim(),
                docCountry: docCountry.trim().toUpperCase(),
              }
            : { reason }),
        }),
      });
      const payload = await res.json().catch(() => null);
      if (!res.ok) {
        /*
          欄位級的錯誤要拿出來講。

          伺服器的 message 在驗證失敗時是「Invalid request body」——
          那句話對正在審核的人完全沒有用，他不知道是哪一欄、也不知道
          該怎麼改。實際發生過：號碼少了一碼，畫面只說「Invalid request body」。
        */
        const fields = payload?.error?.fields as Record<string, string[]> | undefined;
        const detail = fields
          ? Object.entries(fields)
              .map(([k, v]) => `${k}：${v.join("、")}`)
              .join("；")
          : null;
        throw new Error(detail ?? serverError(payload, t, `HTTP ${res.status}`));
      }
      onDone(approve ? "approved" : "rejected");
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-card nm p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-ink">
            {row.displayName}{" "}
            <span className="font-normal text-ink-muted">@{row.handle}</span>
          </p>
          <p className="mt-0.5 font-mono text-[11px] text-ink-muted">{row.email}</p>
          <p className="mt-1 text-[11px] text-ink-muted">
            {row.countryCode ?? "—"} · {row.docType ?? t("idv.noDocType")} ·{" "}
            {new Date(row.submittedAt).toLocaleString()}
          </p>
        </div>

        {!shown && (
          <button
            type="button"
            onClick={() => setShown(true)}
            className="flex shrink-0 items-center gap-1.5 rounded-xl nm-pressable px-3.5 py-2 text-xs font-medium text-ink"
          >
            <ShieldAlert className="size-3.5" />
            {t("idv.showImages")}
          </button>
        )}
      </div>

      {shown && (
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <Doc id={row.id} side="front" />
          <Doc id={row.id} side="back" />
        </div>
      )}

      {/* ── 核准 ────────────────────────────────────────────── */}
      <div className="mt-5 rounded-card nm-inset p-4">
        <p className="text-[11px] font-semibold text-ink">{t("idv.approve")}</p>
        <p className="mt-1 text-[11px] leading-relaxed text-ink-muted">
          {t("idv.approveLedeA")}{" "}
          <strong className="text-ink-soft">{t("idv.approveLedeEmphasis")}</strong>{" "}
          {t("idv.approveLedeB")}
        </p>

        <div className="mt-3 grid gap-3 sm:grid-cols-3">
          <label className="block">
            <span className="text-[11px] font-medium text-ink-soft">{t("idv.docType")}</span>
            <select
              value={docType}
              onChange={(e) => setDocType(e.target.value)}
              className={field}
            >
              <option value="">{t("idv.choose")}</option>
              {DOC_TYPES.map((d) => (
                <option key={d.value} value={d.value}>
                  {t(d.key)}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="text-[11px] font-medium text-ink-soft">{t("idv.country")}</span>
            <select
              value={docCountry}
              onChange={(e) => setDocCountry(e.target.value)}
              className={field}
            >
              {COUNTRY_CODES.map((code) => (
                <option key={code} value={code}>
                  {countryName(code)}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="text-[11px] font-medium text-ink-soft">
              {t("idv.docNumber")}
              <span className="ml-1 font-normal text-ink-muted">{t("idv.optional")}</span>
            </span>
            <input
              value={docNumber}
              onChange={(e) => setDocNumber(e.target.value)}
              placeholder="A123456789"
              className={`${field} font-mono uppercase`}
            />
          </label>
        </div>

        {/*
          略過號碼的代價要講出來，而且只在真的略過時才講 ——
          常駐的警語會被當成背景，出現在「你正要按下去」的那一刻才有效。
        */}
        {docNumber.trim().length < 4 && (
          <p className="mt-2.5 text-[11px] leading-relaxed text-brand-800">
            {t("idv.noNumberWarn")}
          </p>
        )}

        <button
          type="button"
          disabled={busy || !docType}
          onClick={() => void review(true)}
          className="mt-3 flex items-center gap-1.5 rounded-xl nm-pressable px-4 py-2.5 text-xs font-medium text-ink disabled:opacity-40"
        >
          {busy ? <Loader2 className="size-3.5 animate-spin" /> : <BadgeCheck className="size-3.5" />}
          {t("idv.approveAction")}
        </button>
      </div>

      {/* ── 駁回 ────────────────────────────────────────────── */}
      <div className="mt-3 rounded-card nm-inset p-4">
        <p className="text-[11px] font-semibold text-ink">{t("idv.reject")}</p>
        <p className="mt-1 text-[11px] leading-relaxed text-ink-muted">
          {t("idv.rejectLedeA")}{" "}
          <strong className="text-ink-soft">{t("idv.rejectLedeEmphasis")}</strong>{" "}
          {t("idv.rejectLedeB")}
        </p>

        <select value={reason} onChange={(e) => setReason(e.target.value)} className={`${field} mt-3`}>
          <option value="">{t("idv.chooseReason")}</option>
          {REJECT_REASONS.map((code) => (
            <option key={code} value={code}>
              {t(code)}
            </option>
          ))}
        </select>

        <button
          type="button"
          disabled={busy || !reason}
          onClick={() => void review(false)}
          className="mt-3 flex items-center gap-1.5 rounded-xl nm-pressable px-4 py-2.5 text-xs font-medium text-ink disabled:opacity-40"
        >
          <X className="size-3.5" />
          {t("idv.rejectAction")}
        </button>
      </div>

      {error && (
        <p className="mt-3 rounded-xl nm-inset px-4 py-3 text-xs leading-relaxed text-brand-800">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * 一面證件。
 *
 * ── 為什麼用 fetch 而不是 <img src="/api/..."> ──────────────
 *
 * 因為 <img> 失敗時只會給你一個破圖示。它不會告訴你是 401、404、
 * 500，還是回了一個不是圖片的東西 —— 而這支端點跨了兩層
 * （identity → service binding → 後台 Worker），任何一層出問題
 * 症狀都一模一樣。
 *
 * 那個沉默讓這個 bug 查了三輪。用 fetch 的話，錯誤訊息會直接
 * 印在畫面上，下一次三十秒就知道問題在哪。
 *
 * 影像本身是 JSON 裡的 data URL，不是二進位回應 ——
 * 理由見 identity 那一端的說明。
 */
function Doc({ id, side }: { id: string; side: "front" | "back" }) {
  const { t } = useI18n();
  const [src, setSrc] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`/api/identity/${id}/${side}`, { credentials: "same-origin" })
      .then(async (res) => {
        const payload = await res.json().catch(() => null);
        if (!res.ok) {
          throw new Error(serverError(payload, t, `HTTP ${res.status}`));
        }
        if (!payload?.data?.dataUrl) throw new Error(t("idv.noImage"));  // i18n-ignore：診斷訊息，只有審核者會看到，內容要留原文以便回報
        return payload.data.dataUrl as string;
      })
      .then((url) => !cancelled && setSrc(url))
      .catch((e: Error) => !cancelled && setError(e.message));
    return () => {
      cancelled = true;
    };
  }, [id, side]);

  const label = side === "front" ? t("idv.front") : t("idv.back");

  return (
    <figure className="rounded-card nm-inset p-2">
      {error ? (
        <div className="flex min-h-32 items-center justify-center rounded-xl px-4 py-6 text-center">
          <p className="text-xs leading-relaxed text-brand-800">
            {t("idv.loadFailed")}
            <br />
            <span className="font-mono text-[11px] text-ink-muted">{error}</span>
          </p>
        </div>
      ) : src ? (
        <img src={src} alt={t("idv.docAlt", { side: label })} className="w-full rounded-xl" />
      ) : (
        <div className="min-h-32 rounded-xl shimmer" />
      )}
      <figcaption className="mt-2 text-center text-[11px] text-ink-muted">{label}</figcaption>
    </figure>
  );
}
